import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AuthContext } from '@/context/auth-context'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { SettingsViewTrigger } from '@/analytics/enums/settings-modal.enums'
import { getOrganizationAnalyticsId, resolveOrganizationRosterEntrySource, trackOrganizationEvent } from '@/analytics/utils/organization-analytics'
import { isSettingsModalSection, resolveSettingsEntrySource, trackSettingsModalViewed } from '@/analytics/utils/settings-entry-analytics'
import { SettingsModal as SettingsModalShell } from '@/settings/components/SettingsModal'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { closeSettingsModal, setSettingsModalSection } from '@/store/slices/ui-state.slice'
import { useSettingsModalSections } from './hooks/useSettingsModalSections'
import { getDefaultSectionId, getSettingsGroupLabel, isSectionAvailable, type SettingsSectionId } from './nav/settings-nav-sections'
import { SettingsPanelHost } from './panels/SettingsPanelHost'

/**
 * Settings-revamp container (Release 1). Owns the app wiring — reads open/active-section from the
 * ui-state slice, derives the role-aware section list, and renders the active panel — and delegates
 * all layout, responsiveness, nav, and the mobile drill-down to the shared FE-827 SettingsModal
 * shell. Mounted once near the dashboard root (see SettingsModalRoot) and gated by
 * SETTINGS_REVAMP_FOUNDATION there, so this component assumes the flag is on.
 */
export function SettingsModal() {
  const dispatch = useAppDispatch()
  const { userAccountData } = useContext(AuthContext)
  const { trackEvent } = useAnalytics()
  const open = useAppSelector((state: RootState) => state.uiState.settingsModalOpen)
  const storedSection = useAppSelector((state: RootState) => state.uiState.settingsModalSection)
  const entrySource = useAppSelector((state: RootState) => state.uiState.settingsModalEntrySource)
  const rosterEntrySource = useAppSelector((state: RootState) => state.uiState.settingsModalRosterEntrySource)
  const sections = useSettingsModalSections()
  const [visibleSectionId, setVisibleSectionId] = useState<SettingsSectionId | null>(null)
  const viewStateRef = useRef({ hasOpened: false, lastSection: null as string | null, rosterFiredFor: null as string | null })
  const handleVisibleSectionChange = useCallback((sectionId: SettingsSectionId | null) => {
    setVisibleSectionId((current) => (current === sectionId ? current : sectionId))
  }, [])

  // Org accounts split the nav into the member's "Personal settings" and the org's own group (labeled
  // with the org name); individuals get a single "Settings" group. See the Figma per-role nav.
  const isOrgAccount = !!userAccountData?.isOrgAccount
  const orgName = userAccountData?.orgData?.name ?? null
  const groupLabel = (group: number) => getSettingsGroupLabel(group, { isOrgAccount, orgName })

  // Validate the stored section against the current role (it may be stale after a role change or null
  // when opened without a target), falling back to the first section.
  const activeSectionId: SettingsSectionId | null = isSectionAvailable(sections, storedSection) ? storedSection : getDefaultSectionId(sections)

  // True when a specific, valid section was explicitly requested — tells the shell to open mobile
  // directly on the section content rather than the nav list.
  const wasExplicitlyTargeted = storedSection !== null && isSectionAvailable(sections, storedSection)

  useEffect(() => {
    if (!open || !visibleSectionId) {
      if (!open) viewStateRef.current = { hasOpened: false, lastSection: null, rosterFiredFor: null }
      else viewStateRef.current.lastSection = null
      return
    }
    if (!isSettingsModalSection(visibleSectionId)) return

    const viewState = viewStateRef.current
    const settingsEntrySource = resolveSettingsEntrySource(entrySource)
    if (!viewState.hasOpened) {
      trackSettingsModalViewed(trackEvent, {
        settingsSection: visibleSectionId,
        settingsEntrySource,
        settingsViewTrigger: SettingsViewTrigger.Open,
      })
      viewState.hasOpened = true
      viewState.lastSection = visibleSectionId
    } else if (viewState.lastSection !== visibleSectionId) {
      trackSettingsModalViewed(trackEvent, {
        settingsSection: visibleSectionId,
        settingsEntrySource,
        settingsViewTrigger: SettingsViewTrigger.SectionChange,
      })
      viewState.lastSection = visibleSectionId
    }

    // Roster only for an open that landed on Members, or a later open that names a new roster
    // source. A nav click into Members does not change that source, so it does not emit again.
    if (visibleSectionId === 'members' && rosterEntrySource && viewState.rosterFiredFor !== rosterEntrySource) {
      trackOrganizationEvent(trackEvent, AnalyticsEvent.OrganizationRosterOpened, {
        organizationId: getOrganizationAnalyticsId(userAccountData),
        organizationRosterEntrySource: resolveOrganizationRosterEntrySource(rosterEntrySource),
      })
      viewState.rosterFiredFor = rosterEntrySource
    }
  }, [open, visibleSectionId, entrySource, rosterEntrySource, trackEvent, userAccountData])

  // No sections means no account to show (e.g. anonymous); render nothing.
  if (!activeSectionId) return null

  return (
    <SettingsModalShell
      open={open}
      onOpenChange={(next) => {
        if (!next) dispatch(closeSettingsModal())
      }}
      sections={sections}
      activeSectionId={activeSectionId}
      onSectionChange={(id) => dispatch(setSettingsModalSection(id))}
      groupLabel={groupLabel}
      openOnSection={wasExplicitlyTargeted}
      onVisibleSectionChange={handleVisibleSectionChange}
    >
      <SettingsPanelHost sectionId={activeSectionId} />
    </SettingsModalShell>
  )
}
